'use client';

import * as React from 'react';
import {
  Button,
  Flex,
  List,
  ListItem,
  Section,
  Spinner,
  Text,
} from '@highereducation/hen-ui';
import { Fields } from './fields';
import { getFieldValues, getTagularFieldData, sendFormRequest } from './submit';
import type {
  FieldError,
  FormConfirmation,
  GravityFormClientProps,
} from './types';

/**
 * Tagular schema `@type` for the `FormSubmitted` beam. Kept as the single local
 * source of truth (hen-site's canonical emitter resolves this from a registry;
 * `features` can't import the app, so this constant stands in until eventing
 * converges — see the eventing convergence follow-up).
 */
const FORM_SUBMITTED_BEAM_TYPE = 'redventures.usertracking.v3.FormSubmitted';

/** Reports a swallowed eventing error to New Relic when the browser agent is loaded. */
function noticeEventingError(error: unknown): void {
  const nr = (window as unknown as { newrelic?: { noticeError?: Function } })
    .newrelic;
  nr?.noticeError?.(error instanceof Error ? error : new Error(String(error)), {
    component: 'GravityForm',
    action: 'formSubmittedBeam',
  });
}

/** Fires the `FormSubmitted` Tagular beam when `window.tagular` is present (no-op otherwise). */
function fireFormSubmitted(payload: unknown): void {
  if (typeof window === 'undefined') return;
  const win = window as Window & {
    tagular?: (channel: string, ...args: unknown[]) => void;
  };
  try {
    win.tagular?.('beam', 'FormSubmitted', {
      '@type': FORM_SUBMITTED_BEAM_TYPE,
      ...(payload as Record<string, unknown>),
    });
  } catch (error) {
    // Eventing must never break form submission — surface it instead of swallowing.
    noticeEventingError(error);
  }
}

/**
 * Returns `url` only when it is relative or resolves to the same origin as
 * `origin`; otherwise `undefined`. Protocol-relative (`//host`) and absolute
 * cross-origin URLs are rejected to prevent an open-redirect via CMS config.
 */
function safeSameOriginUrl(url: string, origin: string): string | undefined {
  try {
    const resolved = new URL(url, origin);
    return resolved.origin === origin ? resolved.href : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Resolves a confirmation to an inline success message or a same-origin
 * redirect. A CMS-configured cross-origin `url` is ignored (falls back to the
 * inline message) so it can never be used as an open-redirect vector.
 */
function resolveConfirmation(
  confirmation: FormConfirmation | undefined,
  origin: string,
): {
  redirectUrl?: string;
  message: string;
} {
  const message = confirmation?.message || 'Successfully Sent!';
  if (
    (confirmation?.type === 'REDIRECT' || confirmation?.type === 'PAGE') &&
    confirmation.url
  ) {
    const redirectUrl = safeSameOriginUrl(confirmation.url, origin);
    if (redirectUrl) return { redirectUrl, message };
  }
  return { message };
}

/**
 * Client renderer for `[hen-ui-gravity-form]`. Owns form state, submission,
 * confirmations, and the error banner. Styling is HEN-token driven (no Sonic CSS).
 */
export function GravityFormClient({
  id,
  title,
  description,
  fields,
  confirmations,
  submitButton,
  hasHoneypot,
  submit,
}: GravityFormClientProps) {
  const [formErrors, setFormErrors] = React.useState<{ message: string }[]>();
  const [fieldErrors, setFieldErrors] = React.useState<FieldError[]>();
  const [isPosting, setIsPosting] = React.useState(false);
  const [succeeded, setSucceeded] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState<string>();

  const formElementId = `gravity-form-${id}`;
  const honeypotName = `input_${id}_${Math.max(0, ...fields.map((f) => f.id)) + 1}`;

  const handleConfirmations = () => {
    const { redirectUrl, message } = resolveConfirmation(
      confirmations[0],
      window.location.origin,
    );
    if (redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }
    setSuccessMessage(message);
    setSucceeded(true);
    setFieldErrors(undefined);
    setFormErrors(undefined);
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (
    event,
  ) => {
    event.preventDefault();
    setIsPosting(true);

    const formData = new FormData(event.currentTarget);
    const fieldValues = getFieldValues(formData, fields, id);

    fireFormSubmitted({
      outboundUrl: confirmations[0]?.url || '',
      formContext: {
        formId: String(id),
        formName: title,
        formType: confirmations[0]?.type,
      },
      field: getTagularFieldData(fields, formData, id),
    });

    try {
      const { data, errors } = await sendFormRequest(
        submit.cmsUrl,
        submit.query,
        id,
        fieldValues,
      );

      let hasErrors = false;
      if (errors?.length) {
        setFormErrors(errors);
        hasErrors = true;
      }
      if (data?.errors?.length) {
        setFieldErrors(data.errors);
        hasErrors = true;
      }
      if (hasErrors) {
        setIsPosting(false);
        return;
      }

      handleConfirmations();
      setIsPosting(false);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : `An unknown error occurred handling form submit: ${String(error)}`;
      setFormErrors([{ message }]);
      setIsPosting(false);
    }
  };

  const didError = Boolean(formErrors?.length || fieldErrors?.length);
  const showHeader = !succeeded && (title || description);

  if (succeeded) {
    return (
      <Section as='div' padding='none' gap='xxs' role='status'>
        <Text tag='p' variant='b3' id={formElementId}>
          {successMessage}
        </Text>
      </Section>
    );
  }

  return (
    <Flex direction='column' gap='md' width='full'>
      {didError && (
        <div
          className='w-full border border-error-500 bg-error-100 px-md py-sm text-body'
          role='alert'
        >
          <Text tag='p' variant='b3' weight='bold' color='heading'>
            There was a problem with your submission. Please review the fields
            below.
          </Text>
          {Boolean(formErrors?.length) && (
            <List bulletStyle='bullet' className='mt-xxs'>
              {formErrors?.map((error, index) => (
                <ListItem key={index} className='text-b5 text-error-500'>
                  {error.message}
                </ListItem>
              ))}
            </List>
          )}
        </div>
      )}

      {showHeader && (
        <Flex direction='column' gap='xxs' className='text-center'>
          {title && (
            <Text tag='p' variant='b1' weight='bold' color='heading'>
              {title}
            </Text>
          )}
          {description && (
            <Text tag='p' variant='b4'>
              {description}
            </Text>
          )}
        </Flex>
      )}

      <form id={formElementId} onSubmit={handleSubmit} noValidate={false}>
        <Flex direction='column' gap='md'>
          <Fields fields={fields} formId={id} errors={fieldErrors} />

          {hasHoneypot && (
            <input
              type='text'
              name={honeypotName}
              tabIndex={-1}
              autoComplete='off'
              aria-hidden='true'
              className='hidden'
            />
          )}

          <Flex
            direction='row'
            align='center'
            justify='center'
            gap='md'
            className='pt-xs'
          >
            <Button type='submit' disabled={isPosting}>
              {submitButton?.text || 'Submit'}
            </Button>
            {isPosting && <Spinner />}
          </Flex>
        </Flex>
      </form>
    </Flex>
  );
}
